import { RestQueryConfig } from "gatherers/types"
import { AgentArguments, Artifact, RunWorkflowResponse, ArtifactKind } from "./types"

interface CreateArtifactGathererParams {
  pinnedId: string
  agentArguments: AgentArguments
  accountAlias: string
  kind?: ArtifactKind
}

/**
 * Creates a REST query configuration for fetching an AI artifact
 *
 * @param pinnedId - The ID of the pinned workflow to run
 * @param agentArguments - Arguments to pass to the AI agent
 * @param accountAlias - Account alias for API route
 * @param kind - The artifact type to select (plot or table) - matches what user originally pinned
 * @returns REST query configuration for use with RestDataIntegrator
 */
export function createArtifactGatherer({
  pinnedId,
  agentArguments,
  accountAlias,
  kind,
}: CreateArtifactGathererParams): RestQueryConfig<Artifact> {
  const mergedArguments: AgentArguments = {
    ...agentArguments,
    accountAlias,
  }

  // Create a serializable cache key with explicit primitive values
  // This ensures React Query properly detects changes in dashboard parameters
  const cacheKey = [
    "ai-artifact",
    pinnedId,
    {
      modelId: agentArguments.modelId,
      positionSetId: agentArguments.positionSet?.id,
      positionSetType: agentArguments.positionSet?.type,
      baseId: agentArguments.base?.id,
      baseType: agentArguments.base?.type,
      startDate: agentArguments.startDate,
      endDate: agentArguments.endDate,
      accountAlias,
    },
  ]

  return {
    key: cacheKey,
    url: `/api/${accountAlias}/ai/run-pinned-workflow`,
    method: "POST",
    payload: {
      pinnedId,
      agentArguments: mergedArguments,
    },
    select: (data: unknown): Artifact | null => {
      if (!data || typeof data !== "object") {
        return null
      }

      const response = data as RunWorkflowResponse

      // Validate response has artifacts array
      if (!Array.isArray(response.artifacts) || response.artifacts.length === 0) {
        return null
      }

      // If kind is specified, find the matching artifact type
      // (plot workflows return both table and plot, need to select the right one)
      if (kind) {
        const artifact = response.artifacts.find((a) => a.type === kind)
        if (artifact) {
          return artifact
        }
      }

      // Fallback to first artifact if kind not specified or not found
      return response.artifacts[0] || null
    },
    // Use cached data without refetching unless the cache key changes
    // This is crucial for optimistic updates: we seed the cache with current dashboard params,
    // and only refetch when those params actually change
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after unmount
  }
}
